import type FVector from '@data/geometry/FVector';
import { getVectorialScalarMode } from '@gui/store/persistent/persistentStore';
import Grid from 'Geodata/grid';
import { SensorData } from 'Simulations/SensorPoint';

export class SensorDataManager {
    private readonly values: Float32Array;

    private readonly directions: FVector[];

    private scalarMinMax: [number, number];
    private vectorialMinMax: [number, number];

    constructor(values: Float32Array, directions: FVector[]) {
        this.values = values;
        this.directions = directions;
        this.scalarMinMax = this.computeScalarMinMax(values);
        this.vectorialMinMax = this.computeVectorialMinMax(directions);
    }

    public getSensorData(): SensorData[] {
        const kindOfValue: 0 | 1 = getVectorialScalarMode?.() ?? 1;
        const output = [];
        for (let i = 0; i < this.values.length; i++) {
            const sensorPoint = Grid.notNullSensorPoints[i];
            const value = kindOfValue === 0 ? this.directions[i].mag() : this.values[i];
            const sensorData = new SensorData(sensorPoint, value, this.directions[i]);
            output.push(sensorData);
        }
        return output;
    }

    getScalarMinMax(): [number, number] {
        return this.scalarMinMax;
    }

    getVectorialMinMax(): [number, number] {
        return this.vectorialMinMax;
    }

    getScalarValues(): Float32Array {
        return this.values;
    }

    getVectorialValues(): FVector[] {
        return this.directions;
    }

    private computeScalarMinMax(values: Float32Array): [number, number] {
        if (values.length === 0) return [0, 0];

        let min = Infinity;
        let max = -Infinity;
        for (const value of values) {
            if (value < min) min = value;
            if (value > max) max = value;
        }
        return [min, max];
    }

    private computeVectorialMinMax(directions: FVector[]): [number, number] {
        if (directions.length === 0) return [0, 0];

        let min = Infinity;
        let max = -Infinity;
        for (const direction of directions) {
            const magnitude = direction.mag();
            if (magnitude < min) min = magnitude;
            if (magnitude > max) max = magnitude;
        }
        return [min, max];
    }
}
